import { Field, Form, Formik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfile } from '../../../@posturinn/client-react';
import { fetchRefunds, sendBankInfo } from '../../../actions/refundsActions';
import myPostWrapper from '../../../hoc/myPostWrapper';
import IUserProfileReducer from '../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../reducers';
import FormikFieldWrapper from '../../base/forms/formikFieldWrapper';
import { Input } from '../../base/forms/input';
import { Column, Columns } from '../../base/grid';
import { H1 } from '../../base/typography';
import Button from '../../button-v2';
import { RawLoader } from '../../loading/loading';
import { IRefund, refundSchema } from './refund.schema';
import * as StyledRecipients from './refund.styles';

interface Props {}

const Refund: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const [errorMessage, setErrorMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [showForm, setShowForm] = useState(false);
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile }: { userProfile: UserProfile } = userProfileReducer;

	const { refund, refundsLoading, refundsError } = useSelector(({ refundsReducer }) => refundsReducer);

	// refund is fetched once in the menu component

	const refundInitialValues: IRefund = {
		IdentificationNumber: '',
		AccountBank: '',
		AccountType: '',
		AccountNumber: '',
		AccountIdentificationNumber: refund?.identificationNumber,
	};

	useEffect(() => {
		dispatch(fetchRefunds());
	}, [dispatch, showForm]);

	useEffect(() => {
		if (
			refund &&
			refund.hasBeenCorrected === false &&
			refund.totalAmount !== 0 &&
			refund.hasSubmittedBankInfo === false
		) {
			setShowForm(true);
		}
	}, [refund]);

	if (refundsLoading || refundsError) {
		return;
	}

	const checkForm = () => {
		if (refund && refund.hasBeenCorrected === false && refund.totalAmount !== 0) {
			setShowForm(true);
		}
	};

	const handleSubmit = async values => {
		setIsLoading(true);
		values.IdentificationNumber = userProfile.identificationNumber;
		try {
			await dispatch(sendBankInfo(values));
			setErrorMessage('');
		} catch (error) {
			setErrorMessage(error.info.details);
		}
		setIsLoading(false);
		setShowForm(false);
	};

	if (isLoading) return <RawLoader />;

	return (
		<>
			<div>
				<H1 marginBottom="3rem">{t('navigation.refund')}</H1>
				{refund && refund.totalAmount !== 0 && refund.hasBeenCorrected === false && (
					<Columns>
						<Column>
							<p>{String(t('refunds.refundExplanation1'))}</p>
						</Column>
						<Column>
							<p>
								{String(
									t('refunds.refundExplanation2', {
										refundAvailable: refund.amountUncorrected,
										currency: refund.currency,
									})
								)}
							</p>
						</Column>
						<Column>
							<p>{String(t('refunds.refundApology'))}</p>
						</Column>
					</Columns>
				)}
				<StyledRecipients.StyledColumns>
					{refund && refund.totalAmount !== 0 && refund.hasBeenCorrected === false && showForm === true ? (
						<Formik
							initialValues={refundInitialValues}
							validationSchema={refundSchema}
							onSubmit={handleSubmit}>
							{({ handleSubmit }) => (
								<Form onSubmit={handleSubmit}>
									<StyledRecipients.Wrapper>
										<Columns>
											<Column>
												<StyledRecipients.StyledErrorMessage>
													{errorMessage}
												</StyledRecipients.StyledErrorMessage>
											</Column>

											<Column tablet={1 / 3}>
												<FormikFieldWrapper
													name="AccountBank"
													label={t('registerShipment:step-optional-services.bank')}
													languageComponent="">
													<Field
														name="AccountBank"
														as={Input}
														type="number"
														id="AccountBank"
														placeholder={t('registerShipment:step-optional-services.bank')}
													/>
												</FormikFieldWrapper>
											</Column>
											<Column tablet={1 / 3}>
												<FormikFieldWrapper
													name="AccountType"
													label={t('registerShipment:step-optional-services.account-type')}
													languageComponent="">
													<Field
														name="AccountType"
														as={Input}
														type="number"
														id="AccountType"
														placeholder={t(
															'registerShipment:step-optional-services.account-type'
														)}
													/>
												</FormikFieldWrapper>
											</Column>
											<Column tablet={1 / 3}>
												<FormikFieldWrapper
													name="AccountNumber"
													label={t('registerShipment:step-optional-services.account')}
													languageComponent="">
													<Field
														name="AccountNumber"
														as={Input}
														type="number"
														id="AccountNumber"
														placeholder={t(
															'registerShipment:step-optional-services.account'
														)}
													/>
												</FormikFieldWrapper>
											</Column>
											<Column>
												<FormikFieldWrapper
													name="AccountIdentificationNumber"
													label={t('registerShipment:step-info.ssn')}
													languageComponent="">
													<Field
														name="AccountIdentificationNumber"
														as={Input}
														type="number"
														id="AccountIdentificationNumber"
														placeholder={t('registerShipment:step-info.ssn')}
													/>
												</FormikFieldWrapper>
											</Column>
										</Columns>
									</StyledRecipients.Wrapper>

									{refund && refund.hasBeenCorrected === false && (
										<Button title={t('submit')} onClick={handleSubmit} alignRight />
									)}
								</Form>
							)}
						</Formik>
					) : (
						<StyledRecipients.StyledErrorMessage>{errorMessage}</StyledRecipients.StyledErrorMessage>
					)}
				</StyledRecipients.StyledColumns>

				{refund &&
				refund.hasSubmittedBankInfo === true &&
				refund.hasBeenCorrected === false &&
				refund.totalAmount !== 0 &&
				showForm === false ? (
					<Columns>
						<Column tablet={1 / 6}>
							<StyledRecipients.StyledSpan>
								{t('registerShipment:step-optional-services.bank')}
							</StyledRecipients.StyledSpan>
							:{refund.accountBank}
						</Column>
						<Column tablet={1 / 6}>
							<StyledRecipients.StyledSpan>
								{t('registerShipment:step-optional-services.account-type')}
							</StyledRecipients.StyledSpan>
							:{refund.accountType}
						</Column>
						<Column tablet={2 / 6}>
							<StyledRecipients.StyledSpan>
								{t('registerShipment:step-optional-services.account')}
							</StyledRecipients.StyledSpan>
							:{refund.accountNumber}
						</Column>
						<Column>
							<StyledRecipients.StyledSpan>
								{t('registerShipment:step-info.ssn')}
							</StyledRecipients.StyledSpan>
							:{refund.accountIdentificationNumber}
						</Column>
						<Column tablet={1 / 1}>
							<Button title={t('change')} onClick={() => checkForm()} />
						</Column>
					</Columns>
				) : (
					''
				)}
				{refund &&
				refund.hasBeenCorrected === false &&
				refund.hasSubmittedBankInfo === true &&
				refund.totalAmount !== 0 ? (
					<StyledRecipients.StyledMessage>{t('refunds.bankInfoSent')}</StyledRecipients.StyledMessage>
				) : (
					''
				)}

				{refund && refund.hasBeenCorrected === true && refund.totalAmount > 0 ? (
					<StyledRecipients.StyledMessage>
						{t('refunds.refundReceived', {
							refundCorrected: refund.amountCorrected,
							currency: refund.currency,
						})}
					</StyledRecipients.StyledMessage>
				) : (
					''
				)}

				{refund && refund.totalAmount !== 0 && refund.hasBeenCorrected === false ? (
					<StyledRecipients.StyledMessage>
						<p>
							{String(
								t('refunds.refundAvailable', {
									refundAvailable: refund.amountUncorrected,
									currency: refund.currency,
								})
							)}
						</p>
					</StyledRecipients.StyledMessage>
				) : (
					''
				)}
				{refund && refund.totalAmount === 0 && <p>{String(t('refunds.noRefund'))}</p>}
			</div>
		</>
	);
};

export default myPostWrapper(Refund);
